// Libs
import classNames from 'classnames/bind';
import moment from 'moment';
import { Box, Button, InputLabel, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Views, ViewsProps } from 'react-big-calendar';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
// Components, Layouts, Pages
import {
  BaseFilters,
  BaseSelect,
  BaseSplitButton,
  Loading,
  UnderDevelopment,
} from '~/components/common';
import { Calendar, FormScheduleModal, HeaderCalendar } from '~/components/module/schedules';
// Others
import { customerActions } from '~/thunks/customer/customerSlice';
import { DateFormatEnum, EmployeeStatusEnum, RoleEnum, ScheduleTypeEnum } from '~/utils/enum/common';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IFilterSchedule, IParamSchedule, ISchedule } from '~/utils/interface/schedule';
import { getScheduleCustomerList } from '~/thunks/customer/customerThunk';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ZERO,
  DEFAULT_VALUE_FILTER,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { CalendarViewMode } from '~/utils/type/common';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { StorageEnum } from '~/utils/enum/base';
import { mockCustomerScheduleFilterData } from '~/utils/mockData/schedule';
import { IBaseOption, IQueryParamBase } from '~/utils/interface/common';
import { IParamScheduleForCustomerDetail } from '~/utils/interface/customer';
import useDebounce from '~/utils/hooks/useDebounce';
import { getEmployeeList } from '~/thunks/employee/employeeThunk';
import { getFullName } from '~/utils/helper/common';
// Styles, images, icons
import styles from './CustomerSchedule.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const views: ViewsProps = {
  month: true,
  week: true,
  day: true,
};

const CustomerSchedule = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/schedule']);
  const dispatch = useAppDispatch();
  const { id: customerId } = useParams();
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  const {
    scheduleCustomer,
    scheduleCurrentTime,
    isRefreshScheduleCustomer,
    scheduleMode,
    calendarViewMode,
    events,
  } = useAppSelector((state) => state.customer);

  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [scheduleId, setScheduleId] = useState<string>(EMPTY_STRING);
  const [isShowFormScheduleModal, setIsShowFormScheduleModal] = useState<boolean>(false);

  const [isLoadingEmployee, setIsLoadingEmployee] = useState<boolean>(false);
  const [employeeList, setEmployeeList] = useState<IBaseOption[]>([]);
  const [employeeIds, setEmployeeIds] = useState<string[]>([]);
  const [searchKeyEmployee, setSearchKeyEmployee] = useState<string>(EMPTY_STRING);
  const debounceEmployeeSearchKey = useDebounce(searchKeyEmployee, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [valueFilter, setValueFilter] = useState<IFilterSchedule>({
    businessTypes: [DEFAULT_VALUE_FILTER],
    employeeIds: employeeIds || [],
  });

  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  const timeValue = useMemo(() => {
    switch (calendarViewMode) {
      case Views.WEEK:
        const startOfWeek = moment(scheduleCurrentTime).startOf('isoWeek').format('MMMM DD');
        const endOfWeek = moment(scheduleCurrentTime).endOf('isoWeek').format('MMMM DD');
        return `${startOfWeek} - ${endOfWeek}`;
      case Views.DAY:
        return moment(scheduleCurrentTime).format('dddd, MMMM DD');
      default:
        return moment(scheduleCurrentTime).format('MMMM YYYY');
    }
  }, [calendarViewMode, scheduleCurrentTime]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!customerId) return;

    const startDate = moment(scheduleCurrentTime).startOf(calendarViewMode).format('YYYY-MM-DD');
    const endDate = moment(scheduleCurrentTime).endOf(calendarViewMode).format('YYYY-MM-DD');
    const view = calendarViewMode.toUpperCase();

    const queryParam: IParamSchedule = {
      ...(calendarViewMode !== 'month'
        ? { page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: DEFAULT_LIMIT_1000 }
        : {}),
      startDate,
      endDate,
      view,
      businessTypes:
        valueFilter?.businessTypes &&
        valueFilter?.businessTypes?.[DEFAULT_NUMBER_ZERO] !== DEFAULT_VALUE_FILTER
          ? valueFilter?.businessTypes
          : [ScheduleTypeEnum.BOOKING_A_JOB, ScheduleTypeEnum.ESTIMATE],
      employeeIds,
    };

    const payload: IParamScheduleForCustomerDetail = {
      customerId,
      params: queryParam,
    };
    handleGetScheduleCustomer(payload);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleCurrentTime, calendarViewMode, valueFilter]);

  useEffect(() => {
    if (!isRefreshScheduleCustomer || !customerId) return;
    const startDate = moment(scheduleCurrentTime).startOf(calendarViewMode).format('YYYY-MM-DD');
    const endDate = moment(scheduleCurrentTime).endOf(calendarViewMode).format('YYYY-MM-DD');
    const view = calendarViewMode.toUpperCase();

    const queryParam: IParamSchedule = {
      ...(calendarViewMode !== 'month'
        ? { page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: DEFAULT_LIMIT_1000 }
        : {}),
      startDate,
      endDate,
      view,
      businessTypes:
        valueFilter?.businessTypes &&
        valueFilter?.businessTypes?.[DEFAULT_NUMBER_ZERO] !== DEFAULT_VALUE_FILTER
          ? valueFilter?.businessTypes
          : [ScheduleTypeEnum.BOOKING_A_JOB, ScheduleTypeEnum.ESTIMATE],
      employeeIds,
    };

    const payload: IParamScheduleForCustomerDetail = {
      customerId,
      params: queryParam,
    };

    handleGetScheduleCustomer(payload);
    dispatch(customerActions.setIsRefreshScheduleData(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshScheduleCustomer]);

  useEffect(() => {
    const params: IQueryParamBase = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      ...(debounceEmployeeSearchKey && { textSearch: debounceEmployeeSearchKey }),
    };

    handleGetListEmployee(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceEmployeeSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const onSelectEvent = (event: ISchedule) => {
    setScheduleId(event.id);
    setIsShowFormScheduleModal(true);
  };

  const handleCloseFormScheduleModal = () => {
    setIsShowFormScheduleModal(false);
    setScheduleId(EMPTY_STRING);
  };

  const handlePreviousTime = () => {
    dispatch(
      customerActions.setScheduleCurrentTime(
        moment(scheduleCurrentTime).subtract(1, calendarViewMode).format(DateFormatEnum.YYYY_MM_DD)
      )
    );
  };

  const handleNextTime = () => {
    dispatch(
      customerActions.setScheduleCurrentTime(
        moment(scheduleCurrentTime).add(1, calendarViewMode).format(DateFormatEnum.YYYY_MM_DD)
      )
    );
  };

  const handleToday = () => {
    dispatch(customerActions.setScheduleCurrentTime(moment().format(DateFormatEnum.YYYY_MM_DD)));
  };

  const handleGetScheduleCustomer = (payload: IParamScheduleForCustomerDetail) => {
    dispatch(customerActions.changeScheduleCustomer([]));
    dispatch(customerActions.changeEvents([]));
    setIsLoading(true);

    dispatch(getScheduleCustomerList(payload))
      .unwrap()
      .then((res) => {})
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChangeCalendarView = (viewMode: CalendarViewMode) => {
    dispatch(customerActions.changeCalendarViewMode(viewMode));
  };

  const handleShowAddJob = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.createJob}?customerId=${customerId}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.createJob}?customerId=${customerId}`);
        break;
    }
  };

  const handleShowAddEstimate = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.createEstimate}?customerId=${customerId}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.createEstimate}?customerId=${customerId}`);
        break;
    }
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleApplyFilter = (values: IFilterSchedule | undefined) => {
    setValueFilter({
      ...(values?.businessTypes && values?.businessTypes?.[DEFAULT_NUMBER_ZERO] !== DEFAULT_VALUE_FILTER
        ? { businessTypes: values?.businessTypes }
        : {}),
      ...(employeeIds && { employeeIds: employeeIds }),
    });
  };

  const handleSearchEmployeeChange = (searchKey: string) => {
    setSearchKeyEmployee(searchKey);
  };

  const handleFilterEmployeeChange = (value: any) => {
    setEmployeeIds(value);
  };

  const handleGetListEmployee = (params: IQueryParamBase) => {
    setIsLoadingEmployee(true);

    dispatch(getEmployeeList(params))
      .unwrap()
      .then((res) => {
        if (!res || !res?.data) return;

        const { responses } = res?.data;

        if (!responses) return;

        const employeeFiltered = responses?.filter(
          (employee) => employee?.status === EmployeeStatusEnum.ACTIVE
        );

        const employeeOptions = employeeFiltered?.map((employee) => ({
          label: getFullName({ ...employee }),
          value: employee?.id,
        }));

        setEmployeeList(employeeOptions || []);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingEmployee(false);
      });
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} className={cx('container')} id='customerSchedulePage'>
      {/* <BaseToolbar textAction={t('schedule_button_add_schedule')} onAction={handleShowFormSchedule} /> */}

      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'flex-end'}
        className={cx('actionsGroup')}
        gap={'12px'}
      >
        <BaseFilters<IFilterSchedule>
          defaultValue={{ businessTypes: [DEFAULT_VALUE_FILTER], employeeIds: [] }}
          value={{
            businessTypes: valueFilter?.businessTypes || [DEFAULT_VALUE_FILTER],
            employeeIds: employeeIds || [],
          }}
          onApply={handleApplyFilter}
        >
          {({ value, onChange }) => (
            <Stack gap={'12px'}>
              <Typography className={cx('title')}>
                {t('common_action_filter_by', { ns: 'common/base' })}
              </Typography>

              <Stack gap={1}>
                <Box className={cx('filterItem')}>
                  <InputLabel className={cx('label')}>{t('schedule_label_filter_type')}</InputLabel>

                  <BaseSelect
                    layout='horizontal'
                    // label={t('schedule_label_filter_type')}
                    width={'270px'}
                    options={mockCustomerScheduleFilterData}
                    value={value?.businessTypes?.[DEFAULT_NUMBER_ZERO] || EMPTY_STRING}
                    onChange={({ value }) => {
                      onChange?.({ name: 'businessTypes', value: [value] });
                    }}
                  />
                </Box>

                <Box className={cx('filterItem')}>
                  <InputLabel className={cx('label')}>{t('schedule_label_filter_employees')}</InputLabel>

                  <BaseSelect
                    layout='horizontal'
                    // label={t('schedule_label_filter_employees')}
                    multiple
                    isLoading={isLoadingEmployee}
                    width={'270px'}
                    options={employeeList || []}
                    onSearchChange={handleSearchEmployeeChange}
                    value={value?.employeeIds}
                    onChange={handleFilterEmployeeChange}
                  />
                </Box>
              </Stack>
            </Stack>
          )}
        </BaseFilters>

        <BaseSplitButton label={t('schedule_button_add_job')} onClick={handleShowAddJob} primaryWidth={90}>
          {/* <Button variant='text' fullWidth onClick={handleShowAddJob}>
            {t('schedule_button_add_job')}
          </Button> */}

          <Button variant='text' fullWidth onClick={handleShowAddEstimate}>
            {t('schedule_button_add_estimate')}
          </Button>
        </BaseSplitButton>
      </Stack>

      <HeaderCalendar
        views={views}
        timeValue={timeValue}
        calendarViewMode={calendarViewMode}
        onPreviousTime={handlePreviousTime}
        onNextTime={handleNextTime}
        onToday={handleToday}
        onChangeCalendarView={handleChangeCalendarView}
        scheduleMode={scheduleMode}
      />

      <Box className={cx('calendar')}>
        <Calendar
          type='customer'
          scheduleMode={scheduleMode}
          view={calendarViewMode}
          date={scheduleCurrentTime}
          events={events || []}
          timeLineEvents={scheduleCustomer}
          onSelectEvent={onSelectEvent}
        />
      </Box>

      {isShowFormScheduleModal && (
        <FormScheduleModal
          onClose={handleCloseFormScheduleModal}
          scheduleId={scheduleId}
          customerId={customerId}
        />
      )}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />

      {isLoading && <Loading />}
    </Box>
  );
};

export default CustomerSchedule;
